// Table of Specifications editor (one map per quiz). This is where the item →
// Learning Competency link is entered — the single thing the app needs before it
// can report per competency instead of per item.
//
// A competency is its Learning Competency Description: the new curriculum has no
// competency codes, so there is no code field. The "C1/C2" labels in the table are
// positional, for compact reference only.
import type { RenderedView } from '../router.js';
import { navigate } from '../router.js';
import { repos } from '../store.js';
import { esc, onClick, formDialog, toast, confirmDialog, type FieldSpec } from '../dom.js';
import { icon } from '../icons.js';
import {
  parseItemSpec,
  formatItemSpec,
  mapCoverage,
  validateCompetency,
  competencyLabel,
  COGNITIVE_LEVELS,
  MIN_ITEMS_FOR_LEARNER,
  MASTERY_CUTOFF,
} from '../../analysis/competencyMap.js';
import { importTosDialog } from '../tosImport.js';
import type { CompetencyRecord, QuizRecord } from '../../db/schema.js';

/** Add / edit one competency. Returns the row, or null if cancelled or invalid. */
async function competencyDialog(
  quiz: QuizRecord,
  all: CompetencyRecord[],
  editIndex?: number,
): Promise<CompetencyRecord | null> {
  const existing = editIndex != null ? all[editIndex] : undefined;
  const fields: FieldSpec[] = [
    {
      name: 'description',
      label: 'Learning competency',
      type: 'textarea',
      placeholder: 'e.g. Uses context clues to find meaning of unfamiliar words',
      value: existing?.description,
      required: true,
      hint: 'The competency as written in your TOS. This is how the competency is identified — the curriculum no longer uses codes.',
    },
    {
      name: 'items',
      label: 'Item numbers',
      placeholder: 'e.g. 1-3, 7, 14',
      value: existing ? formatItemSpec(existing.items) : '',
      required: true,
      hint: `Which items measure it. Ranges and lists both work. ${MIN_ITEMS_FOR_LEARNER}+ items needed before a single learner's mastery is reported.`,
    },
    {
      name: 'cognitiveLevel',
      label: 'Cognitive level (optional)',
      type: 'select',
      value: existing?.cognitiveLevel ?? '',
      options: [{ value: '', label: '—' }, ...COGNITIVE_LEVELS.map((l) => ({ value: l, label: l }))],
    },
  ];

  const v = await formDialog(existing ? 'Edit competency' : 'Add competency', fields, existing ? 'Save' : 'Add');
  if (!v) return null;

  const spec = parseItemSpec(v.items, quiz.items);
  if (spec.error) {
    toast(spec.error, 'err', 4000);
    return null;
  }
  const candidate: CompetencyRecord = {
    description: v.description.trim(),
    items: spec.items,
    cognitiveLevel: v.cognitiveLevel || undefined,
  };
  const err = validateCompetency(candidate, all, quiz.items, editIndex);
  if (err) {
    toast(err, 'err', 4000);
    return null;
  }
  return candidate;
}

export async function competenciesView(params: Record<string, string>): Promise<RenderedView> {
  const quiz = await repos.getQuiz(params.id);
  if (!quiz) return { title: 'Competencies', back: '/quizzes', html: `<div class="view"><p class="empty">Quiz not found.</p></div>` };
  const map = await repos.competencyMapFor(quiz.id);
  const rows = map?.competencies ?? [];
  const cov = mapCoverage(rows, quiz.items);

  const save = async (competencies: CompetencyRecord[]) => {
    await repos.setCompetencyMap(quiz.id, competencies);
    navigate(location.hash); // re-render in place
  };

  const coverageCard = `<div class="card"><div class="stack">
    <div><span class="big-mps">${cov.mappedItems.length}</span><span class="muted"> / ${quiz.items} items mapped</span>
      ${cov.complete
        ? `<span class="badge badge-ok">${icon.check()} Complete</span>`
        : `<span class="badge badge-warn">${icon.alert()} ${cov.unmappedItems.length} unmapped</span>`}</div>
    <div class="muted" style="font-size:14px">${rows.length} learning competenc${rows.length === 1 ? 'y' : 'ies'} · passing mark ${MASTERY_CUTOFF}%${map?.origin === 'testify' ? ' · from TESTIFY' : ''}</div>
    ${!cov.complete && rows.length
      ? `<p class="hint">Unmapped: <span class="mono">${esc(formatItemSpec(cov.unmappedItems))}</span>. These items still count toward the score, but belong to no competency, so they appear in no competency report.</p>`
      : ''}
    ${cov.duplicateItems.length
      ? `<span class="badge badge-danger">${icon.alert()} Items in two competencies: ${esc(formatItemSpec(cov.duplicateItems))}</span>`
      : ''}
    ${cov.thinCompetencies.length
      ? `<p class="hint">${cov.thinCompetencies.length} competenc${cov.thinCompetencies.length === 1 ? 'y has' : 'ies have'} fewer than ${MIN_ITEMS_FOR_LEARNER} items (${esc(cov.thinCompetencies.join(', '))}). Those are reported for the class but not per learner — too few items to judge one child.</p>`
      : ''}
  </div></div>`;

  const table = rows.length
    ? `<div class="table-wrap"><table class="data"><thead><tr>
         <th></th><th>Learning competency</th><th>Items</th><th class="num">#</th><th>Level</th><th></th>
       </tr></thead><tbody>${rows
         .map(
           (c, i) => `<tr>
             <td class="mono">${competencyLabel(i)}</td>
             <td style="white-space:normal;min-width:240px">${esc(c.description)}</td>
             <td class="mono">${esc(formatItemSpec(c.items))}</td>
             <td class="num">${c.items.length}${c.items.length < MIN_ITEMS_FOR_LEARNER ? ` <span class="badge badge-warn">few</span>` : ''}</td>
             <td>${esc(c.cognitiveLevel ?? '—')}</td>
             <td><button class="btn btn-ghost btn-sm" data-edit="${i}" aria-label="Edit ${competencyLabel(i)}">${icon.edit()}</button>
                 <button class="btn btn-ghost btn-sm" data-del="${i}" aria-label="Delete ${competencyLabel(i)}">${icon.trash()}</button></td>
           </tr>`,
         )
         .join('')}</tbody></table></div>`
    : `<div class="empty">${icon.list()}<p>No competencies yet. Add them one by one, or import your Table of Specifications as a CSV.</p></div>`;

  return {
    title: 'Competencies (TOS)',
    subtitle: `${quiz.title} · ${quiz.items} items`,
    back: `/quiz/${quiz.id}`,
    html: `<div class="view">
      ${coverageCard}
      <p class="section-label">Table of Specifications</p>
      ${table}
      <div class="row2" style="margin-top:12px">
        <button class="btn btn-primary btn-sm" data-add>${icon.plus()} Add competency</button>
        <button class="btn btn-ghost btn-sm" data-import>${icon.upload()} Import TOS (CSV)</button>
      </div>
      ${rows.length ? `<button class="btn btn-ghost btn-block" data-report style="margin-top:10px">${icon.chart()} Item/Competency Analysis</button>` : ''}
      <p class="hint">A competency needs its <strong>item numbers</strong> — that is what turns per-item scores into per-competency results. Each item belongs to at most one competency, so no learner is counted twice.</p>
      ${rows.length ? `<p class="section-label" style="margin-top:22px">Danger zone</p>
        <button class="btn btn-danger btn-block" data-clear>${icon.trash()} Clear this map</button>
        <p class="hint">Removes all ${rows.length} competenc${rows.length === 1 ? 'y' : 'ies'}. Scores and scanned papers are untouched — only the competency reports stop working.</p>` : ''}
    </div>`,
    mount(root) {
      onClick(root, '[data-add]', async () => {
        const c = await competencyDialog(quiz, rows);
        if (c) {
          await save([...rows, c]);
          toast('Competency added');
        }
      });
      onClick(root, '[data-edit]', async (el) => {
        const i = Number(el.dataset.edit);
        const c = await competencyDialog(quiz, rows, i);
        if (c) {
          const next = [...rows];
          next[i] = c;
          await save(next);
          toast('Competency updated');
        }
      });
      onClick(root, '[data-del]', async (el) => {
        const i = Number(el.dataset.del);
        if (!confirmDialog(`Remove ${competencyLabel(i)} from the map? Its items become unmapped.`)) return;
        await save(rows.filter((_, j) => j !== i));
        toast('Competency removed');
      });
      onClick(root, '[data-import]', () => importTosDialog(quiz, rows));
      onClick(root, '[data-report]', () => navigate(`#/quiz/${quiz.id}/gaps`));
      onClick(root, '[data-clear]', async () => {
        if (!confirmDialog(`Clear the Table of Specifications for "${quiz.title}"? ${rows.length} competenc${rows.length === 1 ? 'y' : 'ies'} removed. Scores are not affected.`)) return;
        await repos.deleteCompetencyMap(quiz.id);
        toast('Map cleared');
        navigate(location.hash);
      });
    },
  };
}
